/* eslint-disable react/jsx-key */
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import _get from 'lodash/get';
import clsx from 'clsx';
import { hooks } from '@lifesize/clients.sdk';
import { colors } from '@lifesize/clients.mui-components';
import { featureSelectors } from '@lifesize/nucleus';
import { makeStyles, Theme } from '@material-ui/core';
import { fade } from '@material-ui/core/styles';
import InCallParticipant from './InCallParticipant';
import LowerHandsButton from 'components/InCall/InCallParticipantList/LowerHandsButton';
import AddParticipantButton from 'components/InCall/InCallParticipantList/AddParticipantButton';
import UnMuteAllButton from 'components/InCall/InCallParticipantList/UnMuteAllButton';
import SearchParticipantList from 'components/InCall/InCallParticipantList/SearchParticipantList';
import FilterParticipantsSection from 'components/InCall/InCallParticipantList/FilterParticipantsSection';
import LecturerList from 'components/InCall/InCallParticipantList/LecturerList';
import RemoteModeratorList from 'components/InCall/InCallParticipantList/RemoteModeratorList';
import ModeratorList from 'components/InCall/InCallParticipantList/ModeratorList';
import WaitingRoomList from 'components/InCall/InCallParticipantList/WaitingRoomList';
import { FLAG_WAITING_ROOM } from 'constants/FeatureFlags';
import { PARTICIPANTS_DRAWER } from 'constants/InCall/InCallDrawer';
import { participantFilterType } from 'constants/ParticipantList';

export const getFilteredParticipants = (list: any[], searchTerm: string) => {
  if (!searchTerm) {
    return list;
  }
  // filter the participants that include any part of the searchTerm
  return list.filter((participant) => {
    const searchTerms = searchTerm.trim().toLowerCase().split(' ');
    const nameString = (participant.name || participant.displayName || '').toLowerCase();
    return !!searchTerms.find((term) => nameString.includes(term));
  });
};

const InCallParticipantList = () => {
  const classes = useStyles({});
  const [searchTerm, setSearchTerm] = useState('');
  const [participantFilter, setParticipantFilter] = useState(participantFilterType.none);
  const initialParticipants = hooks.useParticipants();
  const raisedHands = hooks.useParticipantsWithRaisedHands();
  const unmuted = initialParticipants.filter((p) => !(p.localAudioMuteState || p.serverAudioMuteState));
  const participantsCount = _get(initialParticipants, 'length', 0);
  const callFullyConnected = _get(initialParticipants, 'length', 0) > 0;

  const getParticipants = () => {
    switch (participantFilter) {
      case participantFilterType.raisedHands:
        return raisedHands;
      case participantFilterType.unmuted:
        return unmuted;
      default:
        return initialParticipants;
    }
  };

  const conferenceInfo = hooks.useConferenceInfo();
  const participants = getParticipants();
  const lecturers = participants.filter((p) => conferenceInfo && p.uuid && p.uuid === conferenceInfo.lecturerUuid); // should be one or none, but returning an array for consistency
  const moderators = hooks.useModerators();
  const remoteModerators = hooks.useRemoteModerators();

  const isModerator = hooks.useIsCurrentUserModerator();
  const isAudioMutedByServer = conferenceInfo?.serverMuteState;
  const isVisible = useSelector((state) => _get(state, 'app.inCallDrawer.subComponentType')) === PARTICIPANTS_DRAWER;

  const canMuteAll = isModerator && conferenceInfo?.state === 'escalated';
  const isGuest = hooks.useUserInfo()?.isGuest;

  const isWaitingRoomEnabled = hooks.useIsWaitingRoomEnabled();
  const waitingRoomFeatureEnabled = useSelector((state) => !!featureSelectors.getFeatureFlag(state)(FLAG_WAITING_ROOM));

  const handleSearchChange = (searchTerm = '') => {
    setSearchTerm(searchTerm);
  };

  const renderEmptyListMessage = (reason) => {
    switch (reason) {
      case 'searching':
        return (
          <FormattedMessage
            id="components.inCall.participantList.noSearchResults"
            defaultMessage={'No participants matched your search'}
          />
        );
      case participantFilterType.raisedHands:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noRaisedHands"
            defaultMessage={'There are no hands raised'}
          />
        );
      case participantFilterType.unmuted:
        return (
          <FormattedMessage
            id="components.inCall.participantList.noUnmuted"
            defaultMessage={'There are no unmuted participants'}
          />
        );
      default:
        return (
          <FormattedMessage
            id="components.inCall.participantList.loadingParticipants"
            defaultMessage={'Retrieving the participant list...'}
          />
        );
    }
  };

  const participantClassNames = clsx(classes.container, isVisible ? '' : 'isHidden');

  const filteredParticipants = !!searchTerm ? getFilteredParticipants(participants, searchTerm) : participants;

  const filteredModerators = !!searchTerm ? getFilteredParticipants(moderators, searchTerm) : moderators;

  const filteredRemoteModerators = !!searchTerm
    ? getFilteredParticipants(remoteModerators, searchTerm)
    : remoteModerators;

  const filteredLecturers = !!searchTerm ? getFilteredParticipants(lecturers, searchTerm) : lecturers;

  if (!callFullyConnected) {
    return (
      <div className={participantClassNames}>
        <header className="isLoading">
          <h6>
            <FormattedMessage
              id="components.inCall.participantList.loadingParticipants"
              defaultMessage={'Retrieving the participant list...'}
            />
          </h6>
        </header>
      </div>
    );
  }

  return (
    <div className={participantClassNames} data-testid="participant-list-container">
      <SearchParticipantList handleSearchChange={handleSearchChange} searchTerm={searchTerm} />
      <section className="flex">
        {isModerator && isWaitingRoomEnabled && waitingRoomFeatureEnabled && (
          <WaitingRoomList searchTerm={searchTerm} />
        )}
        <div className="listContainer fillRemaining">
          <header>
            <h6 data-testid="participantCount">
              <FormattedMessage
                id="components.inCall.participantList.header"
                description="In call participant list header"
                defaultMessage="Participants ({participants, number})"
                values={{ participants: participantsCount }}
              />
            </h6>
            <FilterParticipantsSection
              participantFilter={participantFilter}
              selectParticipantFilter={setParticipantFilter}
            />
          </header>
          <div className={classes.participantList} data-testid="inCallParticipantList">
            {!filteredParticipants.length && (
              <p className={classes.emptyListMessage} data-testid="emptyParticipantListMessage">
                {renderEmptyListMessage(searchTerm ? 'searching' : participantFilter)}
              </p>
            )}
            {filteredParticipants.map((participant, index) => (
              <InCallParticipant key={`participant-${participant.participantId}-${index}`} participant={participant} />
            ))}
            {participantFilter === participantFilterType.none && (
              <div className={classes.participantRoles}>
                {!!moderators.length && <ModeratorList filteredModerators={filteredModerators} />}
                {!!remoteModerators.length && (
                  <RemoteModeratorList filteredRemoteModerators={filteredRemoteModerators} />
                )}
                {!!lecturers.length && <LecturerList filteredLecturers={filteredLecturers} />}
              </div>
            )}
          </div>
        </div>
      </section>
      <section>
        {canMuteAll && !isAudioMutedByServer && <UnMuteAllButton isMuteAll={true} />}
        {canMuteAll && isAudioMutedByServer && <UnMuteAllButton isMuteAll={false} />}
        {!isGuest && <AddParticipantButton />}
        {isModerator && <LowerHandsButton />}
      </section>
    </div>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    backgroundColor: colors.white,
    color: colors.charcoal,
    display: 'flex',
    flexDirection: 'column',
    fontSize: 10, // TODO: reset to 10 in MuiCssBaseLine (this controls the relative font size of the entire panel)
    height: '100%',
    '&.isHidden': {
      display: 'none'
    },
    '& section': {
      flex: '0 1 0',
      margin: 0,
      '&:first-child': {
        marginTop: theme.spacing(2.5)
      },
      '&.flex': {
        display: 'flex',
        flex: '1 1 auto',
        flexDirection: 'column'
      },
      '& .listContainer': {
        display: 'flex',
        flexDirection: 'column',
        height: 'auto',
        position: 'relative',
        '&.fillRemaining': {
          flex: '1 1 auto'
        },
        '&.fill50': {
          flex: '0 1 auto',
          maxHeight: '50%'
        },
        '&:after': {
          backgroundImage: `linear-gradient(${fade(colors.white, 0)}, ${fade(colors.white, 1)})`,
          bottom: 0,
          content: '',
          height: theme.spacing(2.5),
          left: 0,
          position: 'absolute',
          right: 0
        }
      }
    },
    '& header': {
      color: colors.black,
      flex: '0 0 auto',
      padding: `${theme.spacing(2)}px ${theme.spacing(3)}px 0`,
      textAlign: 'left',
      '& h6': {
        fontSize: '1.6em'
      },
      '&.isLoading': {
        marginTop: 100,
        textAlign: 'center'
      }
    }
  },
  participantList: {
    flex: '1 1 0',
    marginTop: theme.spacing(2),
    overflowX: 'hidden',
    overflowY: 'auto',
    width: '100%'
  },
  emptyListMessage: {
    fontSize: '1.2em',
    padding: `0 ${theme.spacing(3)}px`
  },
  participantRoles: {
    display: 'block',
    flex: '0 0 auto',
    fontSize: '1.2em',
    padding: `${theme.spacing(2)}px ${theme.spacing(3)}px`
  }
}));

export default injectIntl(InCallParticipantList);
